import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { DeviceProvider, Device, Toggleable } from '@codex/gadgets';
import { Icon } from '@codex-brightworld/icons';
import { Slide } from '@codex/animations';
import { Link, Tooltip } from '@codex-brightworld/basics';
import { ShareLink } from '@codex-brightworld/organisms';
import { EnvironmentThemeProvider } from '@codex-brightworld/themes';

const ToggleLink = styled(Link)`
  position: absolute;
  font-family: ${(props) => props.theme.fonts.default.fontFamily};
  right: 0;
  bottom: 15px;
  display: inline-flex;
  z-index: 10;
  @media ${(props) => props.theme.breakpoints.desktop} {
    right: 20px;
  };
  @media ${(props) => props.theme.breakpoints.tabletLayoutLandscape}{
    right: 15px;
  };
  @media ${(props) => props.theme.breakpoints.mobileLayoutPortrait} {
    bottom: 9px;
  };
  :hover {
    color: ${(props) => props.theme.colors.primary.purple};
    text-decoration: none;
  }
`;

const ShareIcon = styled(Icon).attrs({ icon: 'share' })`
  position: relative;
  margin: -3px 0 0 5px;
  width: 24px;
  height: 24px;
  color: ${(props) => props.theme.colors.primary.purple};
  @media ${(props) => props.theme.breakpoints.mobileLayoutPortrait} {
    top: 1px;
  };
  svg path:last-child {
    fill: ${({ theme }) => theme.colors.primary.purple};
  }
  &.dc-icon-share path{
    stroke: none;  
  } 
`;

const StyledSlide = styled(Slide)`
  top: 0;
  right: 6px;
  z-index: 0;
  ${(props) => props.visible && css`
    z-index: 4;
  `} ;
  @media ${(props) => props.theme.breakpoints.desktop} {
    right: 20px;
  };
  @media ${(props) => props.theme.breakpoints.tabletLayoutLandscape} {
    right: 20px;
  };
  @media ${(props) => props.theme.breakpoints.mobileLayoutPortrait} {
    top: 2px;
  };
`;

const SharePanelTooltip = styled(Tooltip).attrs({ arrow: 'top' }, { type: 'info' })`
  width: 240px;
  background-color: #fff;
  padding: 12px 17px !important;
  &:before {
    right: 40px;
  }; 

  height: 145px;
  border-radius: 12px;
  box-shadow: 0 0 6px 0 rgba(33, 48, 56, 0.2);
  border-color: ${(props) => props.theme.colors.neutral.grey[2]};
  border: 0px solid #CCCCCC;
`;

const SharePanelLinks = styled.div`
  column-count: ${(props) => props.columns};
  max-height: 100px;
  margin: 30px 0px 17px 0px;
`;

const SharePanelHeader = styled.div`
  width: 90px;
  height: 18px;
  margin: 1 29px 0 3px;
  font-family: ${(props) => props.theme.fonts.default.fontFamily};
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 3;
  letter-spacing: normal;
  color: ${(props) => props.theme.colors.neutral.currysBlack};
`;

const StyledShareLink = styled(ShareLink)`
  line-height: 2.5;
  .dc-icon {
    margin: -2px -3px 1px 3px;
    stroke: none;
    margin-inline: auto
  }
  color: ${(props) => props.theme.colors.neutral.currysBlack};
  font-family: ${(props) => props.theme.fonts.default.fontFamily};
  :hover {
    color: ${(props) => props.theme.colors.primary.purple};
    text-decoration: none;
  }
`;

const HideLink = styled(Link)`
  position: absolute;
  top: -6px;
  right: 6px;
  padding: 10px;
`;

const HideIcon = styled(Icon)`
  margin: -2px -5px 0 2px;
  width: 20px;
  height: 17px;
  stroke: none;
`;

const WrapperDiv = styled.div`
  overflow: hidden;
`;

const HoverBridge = styled.div`
    position: absolute;
    right: 0;
    top: -15px;
    height: 20px;
    width: 70px;
`;

const SharePanel = (props) => {
  const shareIconLabel = 'Share';
  const shareItemLabel = 'Share this item';

  return (
    <EnvironmentThemeProvider>
      <DeviceProvider>
        <Device>
          {({ isDesktop }) => (
            <Toggleable closeOnOutsideClick>
              {({ open, toggle, innerRef }) => (
                <WrapperDiv
                  ref={innerRef}
                  {...isDesktop ? { onMouseLeave: () => toggle(false) } : {}}
                >
                  <ToggleLink
                    data-interaction={'social'}
                    {...{
                      [isDesktop ? 'onMouseEnter' : 'onClick']: (event) => {
                        event.preventDefault();
                        toggle(!open);
                      }
                    }}
                  >
                    {shareIconLabel}
                    <ShareIcon />
                  </ToggleLink>

                  <StyledSlide
                    visible={open}
                    side={'bottom'}
                    align={'end'}
                  >
                    {isDesktop &&
                      <HoverBridge />}
                    <SharePanelTooltip>
                      <SharePanelHeader>
                        <strong>{shareItemLabel}</strong>

                        {!isDesktop && (
                          <HideLink
                            href={''}
                            onClick={(e) => {
                              e.preventDefault();
                              toggle(false);
                            }}
                          >
                            <HideIcon icon={'close'} />
                          </HideLink>
                        )}
                      </SharePanelHeader>

                      <SharePanelLinks columns={props.links.length > 3 ? 2 : 1}>
                        {props.links.map((link) => (
                          <StyledShareLink
                            key={link.name}
                            service={link.name}
                            shareUrl={link.url}
                          />
                        ))}
                      </SharePanelLinks>
                    </SharePanelTooltip>
                  </StyledSlide>
                </WrapperDiv>
              )}
            </Toggleable>
          )}
        </Device>
      </DeviceProvider>
    </EnvironmentThemeProvider>
  );
};

SharePanel.propTypes = {
  links: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string
  })).isRequired
};

export default SharePanel;
